import React, { useState } from "react";
import { motion } from "framer-motion";
import { Zap } from "react-feather";
import { toast } from "react-toastify"; 
import Cookies from "js-cookie"; 
import { useNavigate, useLocation } from "react-router-dom";
import { Navbar } from "./Navbar";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import axios from "axios";

const api = process.env.REACT_APP_API_URL;

const LoginPage = () => {
  const [useOtp, setUseOtp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const router = useNavigate();
  const location = useLocation();
  const nextRoute = location.state?.next || "/home";  // continue to /upload when coming from "Fit my resume"

  const handleOtpChange = (value, index) => {
    const newOtp = [...otp];
    newOtp[index] = value.slice(0, 1);
    setOtp(newOtp);
    if (value && index < otp.length - 1) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  const handleOtpBackspace = (e, index) => {
    if (e.key === "Backspace" && otp[index] === "") {
      if (index > 0) {
        document.getElementById(`otp-${index - 1}`).focus();
      }
    }
  };

  const handleLogin = async () => {
    setLoading(true);

    try {
      let response;
      if (useOtp) {
        response = await fetch(`${api}auth/login/otp`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, otp: otp.join("") }),
        });
      } else {
        response = await fetch(`${api}auth/login/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, password }),
        });
      }

      const data = await response.json();

      if (response.ok) {
        Cookies.set("access_token", data.access, { expires: 10 });
        localStorage.setItem("userdetails", JSON.stringify(data.user));
        toast.success("Login successful!");
        setTimeout(() => {
          router(nextRoute);
        }, 1000);
      } else {
        toast.error(`Error: ${data.message || "Login failed"}`);
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGitHubSignIn = () => {
    const clientId = process.env.REACT_APP_GITHUB_CLIENT_ID;
    const redirectUri = `${window.location.origin}/auth/github/callback`;
    window.location.href = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=user:email`;
  };

  const handleGoogleSignIn = async (credentialResponse) => {
    try {
      const response = await axios.post(`${api}auth/google-signin/`, {
        id_token: credentialResponse.credential,
      }, { withCredentials: true });

      Cookies.set('access_token', response.data.access, { expires: 10 })
      localStorage.setItem("userdetails", JSON.stringify(response.data.user));
      toast.success("Sign-in successful!");
      setTimeout(() => {
        router(nextRoute);
      }, 1000);
    } catch (error) {
      const msg = error?.response?.data?.error || error?.message || "Unknown error";
      toast.error(`Google Sign-In failed: ${msg}`);
    }
  };

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID}>
      <Navbar />
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-sky-300/30 to-white px-6">
     
        <motion.div
          className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex justify-center items-center space-x-2 mb-2">
            <Zap className="w-8 h-8 text-orange-500" />
            <h2 className="text-2xl font-bold text-gray-800">Resume Advisor</h2>
          </div>

          <h2 className="text-2xl font-bold text-gray-800 text-center mb-6">
            Log In
          </h2>

          <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-300"
              />
            </div>

            {useOtp ? (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Enter OTP
                </label>
                <div className="flex justify-center space-x-2">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      id={`otp-${index}`}
                      type="text"
                      value={digit}
                      onChange={(e) => handleOtpChange(e.target.value, index)}
                      onKeyDown={(e) => handleOtpBackspace(e, index)}
                      className="w-12 h-12 border rounded-md text-center text-lg font-bold focus:outline-none focus:ring focus:ring-blue-300"
                      maxLength={1}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-300"
                />
              </div>
            )}

            <button
              onClick={handleLogin}
              className={`w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-500 transition ${loading ? "cursor-not-allowed opacity-50" : ""}`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex justify-center items-center">
                  <svg
                    className="w-5 h-5 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      strokeWidth="4"
                      className="opacity-25"
                    />
                    <path
                      fill="none"
                      d="M4 12a8 8 0 0 1 8-8 8 8 0 0 1 8 8"
                      className="opacity-75"
                    />
                  </svg>
                </div>
              ) : (
                "Log In"
              )}
            </button>
          </form>

          <div className="flex items-center justify-between mt-6">
            <hr className="w-1/4 border-gray-300" />
            <span className="text-sm text-gray-500">Or log in with</span>
            <hr className="w-1/4 border-gray-300" />
          </div>

          <div className="flex justify-center space-x-4 mt-4">
            <GoogleLogin
              onSuccess={(credentialResponse) => handleGoogleSignIn(credentialResponse)}
              onError={() => toast.error("Google Sign-In failed. Please try again.")}
            />
            <button
              onClick={handleGitHubSignIn}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 transition"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.385-1.335-1.755-1.335-1.755-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.295 24 12c0-6.63-5.37-12-12-12z" />
              </svg>
              GitHub
            </button>
          </div>
          {/* <button
            className="flex items-center px-4 py-2 border rounded-md hover:bg-gray-100"
            onClick={() => handleSocialLogin("LinkedIn")}
          >
            <img
              src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS0bGEl9v47XieEtHyj0TqTr1tOXJmib-KHtw&s"
              alt="LinkedIn"
              className="w-5 h-5 mr-2"
            />
            LinkedIn
          </button> */}

          <p className="mt-4 text-sm text-center text-gray-500">
            Don't have an account?{" "}
            <a href="/signup" className="text-orange-600 hover:underline">
              Sign Up
            </a>
          </p>
        </motion.div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default LoginPage;
